import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Car, 
  Plus, 
  LogOut, 
  ClipboardList, 
  Users, 
  Settings,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronRight,
  FileText,
  HelpCircle
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      const response = await axios.get(`${API}/inspections`, {
        headers: getAuthHeaders()
      });
      setInspections(response.data);
    } catch (error) {
      toast.error("Kon keuringen niet laden");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="status-approved px-3 py-1 font-bold">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Goedgekeurd
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="status-rejected px-3 py-1 font-bold">
            <XCircle className="w-4 h-4 mr-1" />
            Afgekeurd
          </Badge>
        );
      case "pending_review":
        return (
          <Badge className="bg-blue-500 text-white px-3 py-1 font-bold">
            <HelpCircle className="w-4 h-4 mr-1" />
            Ter Beoordeling
          </Badge>
        );
      default:
        return (
          <Badge className="status-pending px-3 py-1 font-bold">
            <Clock className="w-4 h-4 mr-1" />
            In behandeling
          </Badge>
        );
    }
  };

  const stats = {
    total: inspections.length,
    approved: inspections.filter(i => i.status === "approved").length,
    rejected: inspections.filter(i => i.status === "rejected").length,
    pending: inspections.filter(i => i.status === "in_progress").length,
    pendingReview: inspections.filter(i => i.status === "pending_review").length,
  };

  // Simply return kenteken without dashes for consistent display
  const formatKenteken = (kenteken) => {
    if (!kenteken) return "-";
    return kenteken.replace(/-/g, "").toUpperCase();
  };

  // Get country badge styling
  const getCountryBadge = (land) => {
    const countries = {
      NL: { code: "NL", bg: "bg-blue-700", text: "text-white" },
      BE: { code: "BE", bg: "bg-black", text: "text-yellow-400" },
      DE: { code: "DE", bg: "bg-black", text: "text-yellow-400" },
      FR: { code: "FR", bg: "bg-blue-600", text: "text-white" },
      GB: { code: "GB", bg: "bg-blue-900", text: "text-white" },
      OTHER: { code: "INT", bg: "bg-gray-600", text: "text-white" }
    };
    return countries[land] || countries["NL"];
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-safety-orange flex items-center justify-center shadow-lg">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-black text-xl">Rally Auto Advies</h1>
                <p className="text-white/70 text-sm">{user?.name}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 pb-32">
        {/* Stats Grid - Simplified for organisation role */}
        {user?.role === "organisation" ? (
          <div className="grid grid-cols-1 gap-4 mb-8">
            <Card className="hover-lift" data-testid="stat-pending-review">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center">
                    <HelpCircle className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-4xl font-bold text-blue-600">{stats.pendingReview}</p>
                    <p className="text-base text-muted-foreground">Keuringen ter beoordeling</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card 
              className={`hover-lift cursor-pointer transition-all ${activeFilter === null ? 'ring-2 ring-primary' : 'opacity-70 hover:opacity-100'}`}
              onClick={() => setActiveFilter(null)}
              data-testid="stat-total"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">{stats.total}</p>
                    <p className="text-sm text-muted-foreground">Totaal</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={`hover-lift cursor-pointer transition-all ${activeFilter === 'approved' ? 'ring-2 ring-green-500' : 'opacity-70 hover:opacity-100'}`}
              onClick={() => setActiveFilter(activeFilter === 'approved' ? null : 'approved')}
              data-testid="stat-approved"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                    <p className="text-sm text-muted-foreground">Goedgekeurd</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={`hover-lift cursor-pointer transition-all ${activeFilter === 'rejected' ? 'ring-2 ring-red-500' : 'opacity-70 hover:opacity-100'}`}
              onClick={() => setActiveFilter(activeFilter === 'rejected' ? null : 'rejected')}
              data-testid="stat-rejected"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                    <p className="text-sm text-muted-foreground">Afgekeurd</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={`hover-lift cursor-pointer transition-all ${activeFilter === 'in_progress' ? 'ring-2 ring-yellow-500' : 'opacity-70 hover:opacity-100'}`}
              onClick={() => setActiveFilter(activeFilter === 'in_progress' ? null : 'in_progress')}
              data-testid="stat-pending"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                    <p className="text-sm text-muted-foreground">In behandeling</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`hover-lift cursor-pointer transition-all ${activeFilter === 'pending_review' ? 'ring-2 ring-blue-500' : 'opacity-70 hover:opacity-100'}`}
              onClick={() => setActiveFilter(activeFilter === 'pending_review' ? null : 'pending_review')}
              data-testid="stat-pending-review"
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <HelpCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{stats.pendingReview}</p>
                    <p className="text-sm text-muted-foreground">Ter beoordeling</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Admin Links */}
        {user?.role === "admin" && (
          <div className="grid grid-cols-2 gap-4 mb-8">
            <Button 
              variant="outline" 
              className="h-14 justify-start gap-3 hover-lift"
              onClick={() => navigate("/admin/questions")}
              data-testid="admin-questions-link"
            >
              <ClipboardList className="w-5 h-5 text-primary" />
              <span>Keuringsvragen</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-14 justify-start gap-3 hover-lift"
              onClick={() => navigate("/admin/users")}
              data-testid="admin-users-link"
            >
              <Users className="w-5 h-5 text-primary" />
              <span>Gebruikers</span>
            </Button>
          </div>
        )}

        {/* Inspections List */}
        <Card data-testid="inspections-list">
          <CardHeader className="pb-4">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              {user?.role === "organisation" ? "Keuringen Ter Beoordeling" : 
               activeFilter === 'approved' ? "Goedgekeurde Keuringen" :
               activeFilter === 'rejected' ? "Afgekeurde Keuringen" :
               activeFilter === 'in_progress' ? "Keuringen In Behandeling" :
               activeFilter === 'pending_review' ? "Keuringen Ter Beoordeling" :
               "Recente Keuringen"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto"></div>
              </div>
            ) : user?.role === "organisation" && stats.pendingReview === 0 ? (
              <div className="p-8 text-center">
                <HelpCircle className="w-12 h-12 text-blue-400 mx-auto mb-3" />
                <p className="text-muted-foreground">Geen keuringen ter beoordeling</p>
                <p className="text-sm text-muted-foreground/70">Er zijn momenteel geen keuringen die uw beoordeling vereisen</p>
              </div>
            ) : inspections.length === 0 ? (
              <div className="p-8 text-center">
                <Car className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">Nog geen keuringen</p>
                <p className="text-sm text-muted-foreground/70">Start je eerste keuring</p>
              </div>
            ) : (
              <div className="divide-y">
                {inspections
                  .filter(inspection => {
                    // Organisation role only sees "ter beoordeling" inspections
                    if (user?.role === "organisation") {
                      return inspection.status === "pending_review";
                    }
                    // Apply active filter
                    if (activeFilter) {
                      return inspection.status === activeFilter;
                    }
                    return true;
                  })
                  .map((inspection, index) => {
                  const land = inspection.vehicle?.land || "NL";
                  const countryStyle = getCountryBadge(land);
                  
                  return (
                    <button
                      key={inspection.id}
                      className="w-full p-4 hover:bg-muted/50 transition-colors text-left active-press"
                      onClick={() => navigate(`/inspection/${inspection.id}`)}
                      data-testid={`inspection-item-${index}`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      {/* Mobile: Stack layout, Desktop: Row layout */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        {/* Top row on mobile: License plate + Status */}
                        <div className="flex items-center justify-between sm:contents gap-2">
                          {/* License Plate with Country Badge */}
                          <div className="flex items-stretch flex-shrink-0 border-2 border-black rounded overflow-hidden">
                            <div className={`${countryStyle.bg} ${countryStyle.text} px-2 flex items-center text-xs font-bold`}>
                              {countryStyle.code}
                            </div>
                            <div className={`px-3 py-2 flex items-center ${
                              land === "NL" ? 'bg-license-yellow' : 'bg-white'
                            }`}>
                              <span className="font-mono font-bold text-base tracking-wider text-black">
                                {formatKenteken(inspection.vehicle?.kenteken)}
                              </span>
                            </div>
                          </div>
                          
                          {/* Status - visible on mobile in top row */}
                          <div className="flex items-center gap-2 sm:hidden">
                            {getStatusBadge(inspection.status)}
                            <ChevronRight className="w-5 h-5 text-muted-foreground" />
                          </div>
                        </div>
                        
                        {/* Vehicle Info - full width on mobile */}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground truncate">
                            {inspection.vehicle?.merk} {inspection.vehicle?.handelsbenaming}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(inspection.created_at).toLocaleDateString('nl-NL')} - {inspection.inspector_name}
                          </p>
                        </div>
                        
                        {/* Status - hidden on mobile, visible on desktop */}
                        <div className="hidden sm:flex items-center gap-2">
                          {getStatusBadge(inspection.status)}
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Floating Action Button - hidden for organisatie/organisation role */}
      {user?.role !== "organisation" && (
        <div className="fixed bottom-6 right-6 safe-bottom" style={{ zIndex: 9999 }}>
          <Button
            size="lg"
            className="h-16 px-6 bg-safety-orange hover:bg-safety-orange-dark text-white font-bold shadow-2xl shadow-orange-500/40 rounded-2xl active:scale-95 transition-all"
            onClick={() => navigate("/new")}
            data-testid="new-inspection-button"
          >
            <Plus className="w-6 h-6 mr-2" />
            Nieuwe Keuring
          </Button>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
